import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ShieldCheck, Loader2, AlertTriangle, ChevronLeft, CheckCircle2 } from "lucide-react";
import { adminApi } from "../api";

/**
 * Bulk-confirm legacy webpages_Membership registrations
 * (IsConfirmed = 0 → 1). Two-step safety net: preview shows the count
 * + a 10-row sample; the destructive button requires the admin to type
 * "CONFIRMAR" literally.
 */
export default function AdminConfirmClients() {
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(true);
  const [previewErr, setPreviewErr] = useState("");

  const [phrase, setPhrase] = useState("");
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const [runErr, setRunErr] = useState("");

  const loadPreview = async () => {
    setPreviewLoading(true); setPreviewErr("");
    try {
      const r = await adminApi.opsConfirmClientsPreview();
      setPreview(r);
    } catch (e) { setPreviewErr(e.message || "Erro"); }
    finally { setPreviewLoading(false); }
  };
  useEffect(() => { loadPreview(); }, []);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (phrase.trim().toUpperCase() !== "CONFIRMAR") {
      setRunErr("Tem de escrever exactamente CONFIRMAR para autorizar."); return;
    }
    setRunning(true); setRunErr(""); setResult(null);
    try {
      const r = await adminApi.opsConfirmClients({
        confirmation_phrase: "CONFIRMAR", dry_run: false,
      });
      setResult(r);
      // Refresh preview — should now show 0.
      loadPreview();
      setPhrase("");
    } catch (e) {
      setRunErr(e.message || "Erro ao confirmar");
    } finally { setRunning(false); }
  };

  return (
    <div className="max-w-4xl mx-auto p-4" data-testid="admin-confirm-clients">
      <Link to="/admin" className="text-xs text-[#6B7280] hover:text-[#0F1829] inline-flex items-center gap-1 mb-4">
        <ChevronLeft size={12} /> Dashboard
      </Link>

      <header className="mb-6">
        <h1 className="text-2xl font-bold text-[#0F1829] flex items-center gap-2" style={{ fontFamily: "'Fraunces', serif" }}>
          <ShieldCheck size={22} /> Confirmar Registos de Clientes
        </h1>
        <p className="text-sm text-[#6B7280] mt-1">
          Marca em massa os clientes activos com <code className="text-xs bg-[#F5F2EB] px-1 rounded">IsConfirmed=0</code> (tabela legacy <code className="text-xs bg-[#F5F2EB] px-1 rounded">cliente.webpages_Membership</code>) como confirmados. Operação irreversível.
        </p>
      </header>

      {/* Preview */}
      <section className="bg-white border border-[#E6E1D6] rounded-lg p-4 mb-6">
        <h2 className="text-sm font-bold uppercase tracking-[0.12em] mb-3 flex items-center gap-2">
          <span>Pré-visualização</span>
          {previewLoading && <Loader2 size={13} className="animate-spin text-[#6B7280]" />}
        </h2>
        {previewErr && <div className="text-sm text-red-600">{previewErr}</div>}
        {preview && (
          <>
            <div className="mb-3" data-testid="confirm-clients-total">
              <span className="text-3xl font-bold font-mono text-[#0F1829]">
                {(preview.total || 0).toLocaleString("pt-PT")}
              </span>
              <span className="ml-2 text-sm text-[#6B7280]">
                cliente(s) por confirmar.
              </span>
            </div>
            {(preview.sample || []).length > 0 && (
              <div className="overflow-x-auto border border-[#E6E1D6] rounded">
                <table className="w-full text-xs">
                  <thead className="bg-[#FAF8F2] border-b border-[#E6E1D6]">
                    <tr>
                      <th className="text-left px-3 py-2 text-[10px] uppercase tracking-[0.15em] text-[#978D78]">ID</th>
                      <th className="text-left px-3 py-2 text-[10px] uppercase tracking-[0.15em] text-[#978D78]">Nome</th>
                      <th className="text-left px-3 py-2 text-[10px] uppercase tracking-[0.15em] text-[#978D78]">Email</th>
                      <th className="text-left px-3 py-2 text-[10px] uppercase tracking-[0.15em] text-[#978D78]">Registo</th>
                      <th className="text-left px-3 py-2 text-[10px] uppercase tracking-[0.15em] text-[#978D78]">Tipo</th>
                    </tr>
                  </thead>
                  <tbody>
                    {preview.sample.map((c) => (
                      <tr key={c.ID_Cliente} data-testid={`confirm-sample-${c.ID_Cliente}`}
                        className="border-b border-[#F3F0E7]">
                        <td className="px-3 py-2 font-mono">{c.ID_Cliente}</td>
                        <td className="px-3 py-2">{c.NomeCompleto || "—"}</td>
                        <td className="px-3 py-2 text-[#4B5563]">{c.Email || "—"}</td>
                        <td className="px-3 py-2 font-mono">{c.DataRegisto || "—"}</td>
                        <td className="px-3 py-2 font-mono">{c.ID_TipoCliente || "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <p className="text-[11px] text-[#6B7280] mt-2">Amostra das 10 entradas mais recentes (ordenadas por <code>DataRegisto DESC</code>).</p>
          </>
        )}
      </section>

      {/* Result banner */}
      {result && (
        <div className="mb-4 px-4 py-3 bg-emerald-50 border border-emerald-200 rounded text-sm flex items-center gap-2"
          data-testid="confirm-clients-result">
          <CheckCircle2 size={16} className="text-emerald-700" />
          <span><strong>{result.updated}</strong> cliente(s) confirmados com sucesso.</span>
        </div>
      )}

      {/* Destructive action */}
      <section className="bg-[#FFFBEB] border border-[#FCD34D] rounded-lg p-4">
        <h2 className="text-sm font-bold uppercase tracking-[0.12em] mb-2 text-[#92400E] flex items-center gap-2">
          <AlertTriangle size={15} /> Confirmar todos os registos
        </h2>
        <p className="text-sm text-[#92400E] mb-3">
          Esta acção é <strong>irreversível</strong>. Vai actualizar <strong>{preview?.total?.toLocaleString("pt-PT") || "…"}</strong> registos para <code className="bg-white px-1 rounded">IsConfirmed=1</code> e limpar o <code className="bg-white px-1 rounded">ConfirmationToken</code>.
          Para autorizar, escreva <strong>CONFIRMAR</strong> em maiúsculas no campo abaixo.
        </p>
        <form onSubmit={onSubmit} className="flex flex-wrap items-end gap-3">
          <div>
            <label className="block text-[11px] text-[#92400E] mb-1 font-bold">Frase de confirmação</label>
            <input
              type="text" autoComplete="off"
              value={phrase}
              onChange={(e) => setPhrase(e.target.value)}
              data-testid="confirm-phrase-input"
              placeholder="CONFIRMAR"
              className="w-56 px-3 py-1.5 text-sm border border-[#FCD34D] rounded font-mono uppercase bg-white focus:border-[#92400E] outline-none"
            />
          </div>
          <button type="submit" disabled={running || !preview?.total}
            data-testid="confirm-submit"
            className="px-4 py-1.5 text-sm rounded bg-[#92400E] text-white hover:bg-[#7C2D12] disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-1">
            {running ? <Loader2 size={13} className="animate-spin" /> : <ShieldCheck size={13} />}
            Confirmar agora
          </button>
        </form>
        {runErr && (
          <div className="mt-3 px-3 py-2 bg-red-50 border border-red-200 rounded text-sm text-red-700"
            data-testid="confirm-clients-error">{runErr}</div>
        )}
      </section>
    </div>
  );
}
